import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ShoppingCart,
  Check,
  Eye,
  Waves,
  MapPin,
  Battery,
  ChevronLeft,
  ChevronRight,
  Settings2,
} from 'lucide-react';
import { PRODUCT_DATA } from '../staticData';

const ShopPage = () => {
  const [product, setProduct]                 = useState(PRODUCT_DATA);
  const [loading]                             = useState(false);
  const [error]                               = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const galleryImages = [
    {
      url: '/media/product-studio/clearway-stick-studio-front.jpg',
      alt: 'ClearWay Smart Walking Stick - Hero Shot',
      aspectRatio: '1:1'
    },
    {
      url: '/media/product-studio/clearway-stick-studio-front-tall.png',
      alt: 'ClearWay Smart Walking Stick - Studio Shot',
      aspectRatio: '9:16'
    },
    {
      url: '/media/product-studio/clearway-stick-studio-angle.png',
      alt: 'ClearWay Smart Walking Stick - Angled Studio View',
      aspectRatio: '9:16'
    },
    {
      url: 'https://clearway-prod.cdn.jtlr.org/site-images/man-stick.png',
      alt: 'Man using ClearWay Smart Walking Stick',
      aspectRatio: '9:16'
    },
    {
      url: 'https://clearway-prod.cdn.jtlr.org/site-images/woman-stick.png',
      alt: 'Woman using ClearWay Smart Walking Stick',
      aspectRatio: '9:16'
    },
    {
      url: 'https://clearway-prod.cdn.jtlr.org/site-images/stick-park.png',
      alt: 'ClearWay Smart Walking Stick - Outdoor Setting',
      aspectRatio: '9:16'
    }
  ];

  const images =
    product?.image_urls?.length > 0
      ? product.image_urls.map((url, idx) => ({
          url,
          alt: `${product?.name || 'Product'} - Image ${idx + 1}`,
          aspectRatio: '1:1'
        }))
      : galleryImages;

  useEffect(() => {
    const backendUrl =
      process.env.NODE_ENV === 'production'
        ? process.env.REACT_APP_BACKEND_URL || ''
        : '';

    if (backendUrl) {
      fetch(`${backendUrl}/api/product`)
        .then((res) => res.json())
        .then((data) => setProduct(data))
        .catch(() => setProduct(PRODUCT_DATA));
    }
  }, []);

  // Product + Offer structured data, built from the same product state the
  // page renders so the schema price always matches the visible price.
  useEffect(() => {
    if (!product) return;
    const priceValidUntil = new Date();
    priceValidUntil.setFullYear(priceValidUntil.getFullYear() + 1);
    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      // Stable node id so other pages (notably /configure) can reference this
      // one product instead of declaring a second, competing Product entity.
      '@id': 'https://www.clearway-mobility.com/shop#product',
      name: product.name,
      description: product.long_description,
      image: product.image_urls?.length
        ? product.image_urls
        : ['https://www.clearway-mobility.com/media/product-studio/clearway-stick-studio-front.jpg'],
      sku: 'CLEARWAY-STICK-STD',
      mpn: 'CLEARWAY-STICK-STD',
      material: '3D-printed plant-based and renewable plastics',
      countryOfOrigin: { '@type': 'Country', name: 'India' },
      brand: { '@type': 'Brand', name: 'ClearWay' },
      manufacturer: {
        '@type': 'Organization',
        name: 'Clearway Mobility LLP',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '7th Floor, Jolly Maker Chambers II, Nariman Point',
          addressLocality: 'Mumbai',
          postalCode: '400021',
          addressCountry: 'IN'
        }
      },
      category: 'Assistive Technology',
      audience: { '@type': 'PeopleAudience', audienceType: 'Visually impaired and elderly users' },
      additionalProperty: [
        {
          '@type': 'PropertyValue',
          name: 'Regulatory approval status',
          value:
            'Prototype demonstrator device; has not received regulatory approval; provided for evaluation purposes'
        },
        { '@type': 'PropertyValue', name: 'Power source', value: 'Standard replaceable 9V battery' }
      ],
      url: 'https://www.clearway-mobility.com/shop',
      offers: {
        '@type': 'Offer',
        url: 'https://www.clearway-mobility.com/shop',
        priceCurrency: 'INR',
        price: String(product.price_inr),
        priceValidUntil: priceValidUntil.toISOString().slice(0, 10),
        availability: product.in_stock
          ? 'https://schema.org/InStock'
          : 'https://schema.org/OutOfStock',
        itemCondition: 'https://schema.org/NewCondition',
        seller: { '@type': 'Organization', name: 'Clearway Mobility LLP' },
        shippingDetails: [
          {
            '@type': 'OfferShippingDetails',
            shippingRate: { '@type': 'MonetaryAmount', value: '999', currency: 'INR' },
            shippingDestination: { '@type': 'DefinedRegion', addressCountry: 'IN' }
          },
          {
            '@type': 'OfferShippingDetails',
            shippingRate: { '@type': 'MonetaryAmount', value: '135', currency: 'AED' },
            shippingDestination: { '@type': 'DefinedRegion', addressCountry: 'AE' }
          }
        ],
        hasMerchantReturnPolicy: {
          '@type': 'MerchantReturnPolicy',
          applicableCountry: ['IN', 'AE'],
          returnPolicyCountry: ['IN', 'AE'],
          returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
          merchantReturnDays: 30,
          returnMethod: 'https://schema.org/ReturnByMail',
          returnFees: 'https://schema.org/ReturnShippingFees',
          refundType: 'https://schema.org/FullRefund'
        }
      }
    };
    let el = document.getElementById('product-jsonld');
    if (!el) {
      el = document.createElement('script');
      el.id = 'product-jsonld';
      el.type = 'application/ld+json';
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(jsonLd);
    return () => {
      const existing = document.getElementById('product-jsonld');
      if (existing) existing.remove();
    };
  }, [product]);

  useEffect(() => {
    setCurrentImageIndex((prev) => Math.min(prev, Math.max(images.length - 1, 0)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images.length]);

  const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % images.length);
  const prevImage = () => setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  const goToImage = (index) => setCurrentImageIndex(index);

  if (loading) {
    return (
      <div className="min-h-screen pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="skeleton h-96 rounded-3xl" />
        </div>
      </div>
    );
  }

  if (!product || !product.in_stock) {
    return (
      <div className="min-h-screen pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl font-bold mb-6">Product Not Available</h1>
          <p className="text-gray-400 mb-8">
            {error ? `Error: ${error}` : 'Sorry, this product is currently not available.'}
          </p>
          <Link to="/" className="btn-primary px-8 py-4 rounded-full inline-block">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  const features = [
    {
      icon: <Eye className="w-6 h-6" />,
      title: 'Forward Obstacle Detection',
      description: 'Two ultrasonic sensors detect obstacles ahead and alert you via vibration'
    },
    {
      icon: <Waves className="w-6 h-6" />,
      title: 'Surface & Drop Detection',
      description: 'Downward IR sensor identifies potholes, drops, and uneven terrain — alerts via audio'
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: 'Apple Find My (optional)',
      description: 'Locate your Clearway anywhere in Apple\'s Find My network'
    },
    {
      icon: <Battery className="w-6 h-6" />,
      title: 'Reliable Power',
      description: 'No rechargeable batteries — focus on simplicity'
    }
  ];

  const current = images[currentImageIndex] || images[0];

  return (
    <>
      <div className="min-h-screen pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">

          {/* Product Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-24">
            {/* Gallery */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="sticky top-32">
                <div className="rounded-3xl overflow-hidden bg-gradient-to-br from-gray-900 to-black border border-gray-800 relative">
                  <img
                    src={current?.url || 'https://pub-dc1f807246634dbf8b79c15badb0e4af.r2.dev/3.png'}
                    alt={current?.alt || product.name}
                    className="w-full aspect-square object-cover"
                    data-testid="product-image"
                    loading="lazy"
                  />

                  {images.length > 1 && (
                    <>
                      <button
                        type="button"
                        onClick={prevImage}
                        aria-label="Previous image"
                        className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/50 border border-white/10 flex items-center justify-center hover:bg-black/70 transition"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <button
                        type="button"
                        onClick={nextImage}
                        aria-label="Next image"
                        className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/50 border border-white/10 flex items-center justify-center hover:bg-black/70 transition"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                        {images.map((_, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => goToImage(idx)}
                            aria-label={`Go to image ${idx + 1}`}
                            className={[
                              'h-2.5 rounded-full transition-all',
                              idx === currentImageIndex ? 'w-8 bg-white/90' : 'w-2.5 bg-white/40 hover:bg-white/60'
                            ].join(' ')}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {images.length > 1 && (
                  <div className="mt-5 grid grid-cols-6 gap-3">
                    {images.map((img, idx) => (
                      <button
                        key={img.url + idx}
                        type="button"
                        onClick={() => goToImage(idx)}
                        className={[
                          'rounded-2xl overflow-hidden border transition-all',
                          idx === currentImageIndex
                            ? 'border-blue-500/60 ring-2 ring-blue-500/30'
                            : 'border-gray-800 hover:border-gray-700'
                        ].join(' ')}
                        aria-label={`Select image ${idx + 1}`}
                      >
                        <img
                          src={img.url}
                          alt={img.alt}
                          className="w-full aspect-square object-cover"
                          loading="lazy"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>

            {/* Product Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/20 mb-6">
                <Check className="w-4 h-4 text-green-400" />
                <span className="text-green-400 text-sm font-medium">In Stock</span>
              </div>

              <h1 className="text-5xl font-bold mb-4" data-testid="product-name">
                {product.name}
              </h1>

              <p className="text-2xl text-gray-400 mb-8">{product.short_description}</p>

              {/* Pricing */}
              <div className="mb-8">
                <div className="flex items-baseline gap-3">
                  <span className="text-5xl font-bold" data-testid="product-price">
                    ₹{product.price_inr?.toLocaleString()}
                  </span>
                  {product.compare_at_price_inr && (
                    <span className="text-2xl text-gray-600 line-through">
                      ₹{product.compare_at_price_inr?.toLocaleString()}
                    </span>
                  )}
                </div>
                <p className="text-gray-500 mt-2">Price in INR. Shipping calculated at checkout.</p>
              </div>

              {/* Configure & Request CTA */}
              <Link
                to="/configure"
                data-testid="configure-buy-button"
                className="btn-primary w-full py-5 rounded-2xl text-lg font-semibold flex items-center justify-center gap-3 mb-4"
              >
                <Settings2 className="w-5 h-5" />
                Configure &amp; Request
              </Link>
              <p className="text-xs text-gray-500 text-center mb-6">
                Customise your Clearway in 2 minutes, then submit your details — no payment now. Your advisor confirms everything and emails you a payment link.
              </p>

              {/* Shipping Notice */}
              <div className="p-6 rounded-2xl bg-blue-500/5 border border-blue-500/20 mb-6">
                <h3 className="font-semibold mb-2 text-blue-400">International Shipping Available</h3>
                <p className="text-sm text-gray-400 mb-2">
                  We currently ship to India (₹999) and UAE (AED 135).
                </p>
                <p className="text-xs text-gray-500">
                  Orders are billed in the currency of their destination — INR for India, AED for
                  the UAE. Your bank may apply FX conversion. Duties/taxes may apply.
                </p>
              </div>

              {/* Payment & Returns Reassurance */}
              <div className="p-6 rounded-2xl bg-gray-800/30 border border-gray-800 mb-6">
                <h3 className="font-semibold mb-2">Payments &amp; Returns</h3>
                <p className="text-sm text-gray-400 mb-2">
                  Secure prepaid checkout via Razorpay — UPI, credit/debit cards (including
                  international cards), and net banking. 30-day return window from delivery.{' '}
                  <Link to="/refund" className="underline hover:text-gray-300">
                    Read our Refund Policy
                  </Link>
                </p>
                <p className="text-xs text-gray-500">
                  Checkout is completed on clearway-mobility.com, operated by Clearway Mobility LLP.
                </p>
              </div>

              {/* Prototype Notice */}
              <div className="p-4 rounded-xl bg-yellow-500/5 border border-yellow-500/20 mb-8">
                <p className="text-yellow-400/80 text-sm">
                  <strong>Note:</strong> ClearWay is a prototype demonstrator device currently in development.
                  This product has not received regulatory approval and is provided for evaluation purposes.{' '}
                  <Link to="/safety-notice" className="underline hover:text-yellow-300">
                    Read our Safety Notice
                  </Link>
                </p>
              </div>

              {/* Description */}
              <div className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">Product Description</h2>
                <p className="text-gray-400 leading-relaxed">{product.long_description}</p>
              </div>
            </motion.div>
          </div>

          {/* Specifications — labeled facts, one row per spec, machine-readable
              for both buyers and AI/search extraction */}
          <section className="mb-24" aria-labelledby="spec-heading">
            <h2 id="spec-heading" className="text-3xl font-bold text-center mb-12">
              Specifications
            </h2>
            <div className="max-w-3xl mx-auto overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <tbody>
                  {[
                    ['Forward obstacle detection', 'Dual ultrasonic sensors — alerts via vibration'],
                    ['Surface & drop detection', 'Downward infrared sensor for potholes, drops, and uneven terrain — alerts via audio'],
                    ['Location tracking', 'Apple Find My (optional module; requires an Apple device)'],
                    ['Power', 'Standard replaceable 9V battery — no charging required'],
                    ['Materials', '3D-printed plant-based and renewable plastics'],
                    ['Assembly', 'Fully assembled in India'],
                    ['Returns', '30-day return window from delivery'],
                    ['Regulatory status', 'Prototype demonstrator — not yet regulatory approved'],
                  ].map(([label, value]) => (
                    <tr key={label} className="border-b border-gray-800">
                      <th scope="row" className="py-4 pr-6 text-gray-300 font-medium align-top whitespace-nowrap">
                        {label}
                      </th>
                      <td className="py-4 text-gray-400">{value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Features Grid */}
          <section className="mb-24">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl font-bold text-center mb-12"
            >
              Key Features
            </motion.h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="p-6 rounded-2xl bg-gradient-to-b from-gray-800/50 to-transparent border border-gray-800/50 hover:border-blue-500/30 transition-all"
                >
                  <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center text-blue-400 mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                  <p className="text-gray-500 text-sm">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </section>

          {/* CTA Section */}
          <section className="text-center py-16 px-6 rounded-3xl bg-gradient-to-br from-blue-500/10 to-transparent border border-blue-500/20">
            <h2 className="text-3xl font-bold mb-4">Ready to Experience ClearWay?</h2>
            <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
              Configure your Clearway to match exactly how you move.
            </p>
            <Link
              to="/configure"
              data-testid="cta-buy-button"
              className="btn-primary px-10 py-4 rounded-full inline-flex items-center gap-3 text-lg font-semibold"
            >
              <ShoppingCart className="w-5 h-5" />
              Configure &amp; Request
            </Link>
          </section>

        </div>
      </div>
    </>
  );
};

export default ShopPage;
